import { useRef } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { ecosystemCopy, ecosystemItems } from "../config/media";

const EASE = [0.16, 1, 0.3, 1];

export const EcosystemSection = () => {
  const railRef = useRef(null);

  const scrollBy = (dir) => {
    const rail = railRef.current;
    if (!rail) return;
    const card = rail.querySelector("[data-card]");
    const step = card ? card.offsetWidth + 24 : 480;
    rail.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <section
      data-testid="ecosystem-section"
      className="py-20 lg:py-24"
    >
      <div className="px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-120px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="flex items-end justify-between gap-12"
        >
          <div className="max-w-[560px]">
            <p
              data-testid="ecosystem-eyebrow"
              className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--strollee-accent)]"
            >
              {ecosystemCopy.eyebrow}
            </p>
            <h2 className="mt-4 text-[36px] lg:text-[48px] font-semibold leading-[1.1] tracking-[-0.03em] text-[var(--strollee-ink)]">
              {ecosystemCopy.headlineLines.map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))}
            </h2>
          </div>
          <div className="flex items-end gap-10">
            <p className="max-w-[340px] pb-2 text-base leading-relaxed text-[var(--strollee-muted)]">
              {ecosystemCopy.support}
            </p>
            <div className="flex items-center gap-3 pb-2">
              <button
                type="button"
                aria-label="Scroll ecosystem back"
                data-testid="ecosystem-prev-btn"
                onClick={() => scrollBy(-1)}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-[var(--strollee-line)] text-[var(--strollee-ink)] transition-all duration-200 hover:bg-[var(--strollee-surface)] hover:border-[var(--strollee-ink)]/30"
              >
                <ArrowLeft size={17} strokeWidth={2} />
              </button>
              <button
                type="button"
                aria-label="Scroll ecosystem forward"
                data-testid="ecosystem-next-btn"
                onClick={() => scrollBy(1)}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-[var(--strollee-line)] text-[var(--strollee-ink)] transition-all duration-200 hover:bg-[var(--strollee-surface)] hover:border-[var(--strollee-ink)]/30"
              >
                <ArrowRight size={17} strokeWidth={2} />
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
        className="mt-10 lg:mt-12"
      >
        <div
          ref={railRef}
          data-testid="ecosystem-rail"
          className="flex snap-x snap-proximity gap-6 overflow-x-auto px-6 lg:px-10 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {ecosystemItems.map((item) => (
            <article
              key={item.id}
              data-card
              data-testid={`ecosystem-card-${item.id}`}
              className="group w-[400px] lg:w-[440px] shrink-0 snap-start overflow-hidden rounded-[var(--strollee-radius-module)] border border-[var(--strollee-line)]/80 bg-[var(--strollee-surface)] transition-shadow duration-300 hover:shadow-[0_16px_40px_rgba(23,23,19,0.08)]"
            >
              <div className="h-[260px] lg:h-[300px] overflow-hidden">
                <img
                  src={item.image.url}
                  alt={item.image.alt}
                  style={{
                    ...(item.image.position ? { objectPosition: item.image.position } : {}),
                    ...(item.image.filter ? { filter: item.image.filter } : {}),
                  }}
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                  draggable={false}
                />
              </div>
              <div className="p-6 lg:p-7">
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--strollee-accent)]">
                  {item.label}
                </p>
                <h3 className="mt-2.5 text-xl lg:text-[22px] font-semibold tracking-[-0.015em] text-[var(--strollee-ink)]">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-[var(--strollee-muted)]">
                  {item.copy}
                </p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[var(--strollee-ink)] transition-colors duration-200 group-hover:text-[var(--strollee-accent)]">
                  {ecosystemCopy.cardCta}
                  <ArrowUpRight
                    size={14}
                    strokeWidth={2.4}
                    className="transition-transform duration-200 group-hover:translate-x-[2px] group-hover:-translate-y-[2px]"
                  />
                </span>
              </div>
            </article>
          ))}
        </div>
      </motion.div>

      <div className="px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.25 }}
          className="mt-8 lg:mt-10 flex justify-end"
        >
          <a
            href="#system"
            data-testid="ecosystem-end-cta"
            className="group inline-flex items-center gap-2 text-sm font-semibold tracking-wide text-[var(--strollee-ink)] transition-colors duration-200 hover:text-[var(--strollee-accent)]"
          >
            {ecosystemCopy.endCta}
            <ArrowRight
              size={16}
              strokeWidth={2.2}
              className="transition-transform duration-200 group-hover:translate-x-1"
            />
          </a>
        </motion.div>
      </div>
    </section>
  );
};
